import React from 'react';
import { ShieldCheck, FileText, MessageSquareWarning, ExternalLink } from 'lucide-react';
import { MF_DISCLAIMER, COMMISSION_DISCLOSURE, NON_MF_NOTE } from '@/components/ComplianceNotice';
import { CommissionTable } from '@/components/CommissionTable';

const AMFI_COC_URL = 'https://www.amfiindia.com/uploads/Revised_Codeof_Conductfor_Mutual_Fund_Distributors_April2022_57d91fe1c4.pdf';

const Section = ({ icon: Icon, title, testId, children }) => (
  <div className="bg-[#FDF8F5] rounded-2xl p-6 sm:p-8 border border-[#E5E5E5]" data-testid={testId}>
    <div className="flex items-center gap-3 mb-4">
      <Icon className="w-6 h-6 text-[#D97706]" />
      <h2 className="text-xl sm:text-2xl font-heading font-medium text-[#A0451E]">{title}</h2>
    </div>
    <div className="space-y-3 text-sm font-body text-[#4A4A4A] leading-relaxed">{children}</div>
  </div>
);

const ExtLink = ({ href, children, testId }) => (
  <a href={href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 font-semibold text-[#A0451E] hover:text-[#D97706]" data-testid={testId}>
    {children} <ExternalLink className="w-3.5 h-3.5" />
  </a>
);

const Grievance = () => (
  <div className="min-h-screen" data-testid="grievance-page">
    <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8 bg-[#FDF8F5]">
      <div className="max-w-5xl mx-auto">
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-medium text-[#A0451E] mb-6" data-testid="grievance-heading">
          Disclosures & Investor Grievances
        </h1>
        <p className="text-base font-body text-[#4A4A4A] leading-relaxed">
          Important information about who we are, how we are paid and how you can raise a complaint.
        </p>
      </div>
    </section>

    <section className="py-16 bg-white">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
        <Section icon={ShieldCheck} title="Business Identity" testId="identity-section">
          <p className="text-base font-semibold text-[#A0451E]">Impetus Investment Services</p>
          <p>AMFI-registered Mutual Fund Distributor · ARN: 116347</p>
          <p className="italic">Bridge to Financial Wellness</p>
          <p>
            We are not a SEBI-registered Investment Adviser and do not provide investment advisory services under the SEBI (Investment
            Advisers) Regulations, 2013. AMFI registration does not imply approval, recommendation or guarantee of returns.
          </p>
          <p>{NON_MF_NOTE}</p>
        </Section>

        <Section icon={FileText} title="Commission Disclosure" testId="commission-section">
          <p>{COMMISSION_DISCLOSURE}</p>
          <p>
            Investments through us are made in Regular Plans, which include distributor commission. Investors may also invest directly
            with the Asset Management Company through Direct Plans, which have a lower expense ratio and do not involve distributor commission.
          </p>
          <CommissionTable />
          <p>
            Scheme documents (Scheme Information Document, Statement of Additional Information and Key Information Memorandum) are available
            on the respective AMC websites and on <ExtLink href="https://www.amfiindia.com" testId="amfi-link">amfiindia.com</ExtLink>.
          </p>
          <p>
            We follow the <ExtLink href={AMFI_COC_URL} testId="amfi-coc-link">AMFI Code of Conduct for Mutual Fund Distributors</ExtLink>.
          </p>
          <p className="font-semibold">{MF_DISCLAIMER}</p>
        </Section>

        <Section icon={MessageSquareWarning} title="Investor Grievance Redressal" testId="grievance-section">
          <p><strong>Step 1 – Contact us:</strong> Email <a href="mailto:cs@impetusinvestments.in" className="text-[#A0451E] font-semibold">cs@impetusinvestments.in</a> or call 98417 90000 / 73058 35581 (Mon–Fri 9:30 AM–6:00 PM, Sat 9:30 AM–3:00 PM).</p>
          <p data-testid="grievance-officer-placeholder"><strong>Grievance Officer:</strong> [Name] · [Email] · [Phone] – to be provided by Impetus Investment Services</p>
          <p><strong>Step 2 – Asset Management Company:</strong> For complaints about a scheme or transaction, you may also contact the investor service team of the respective AMC.</p>
          <p><strong>Step 3 – SEBI SCORES:</strong> If your complaint is not resolved, you may lodge it on SEBI's complaints portal: <ExtLink href="https://scores.sebi.gov.in" testId="scores-link">scores.sebi.gov.in</ExtLink></p>
          <p><strong>Step 4 – Online Dispute Resolution:</strong> You may also use the SMART ODR portal: <ExtLink href="https://smartodr.in" testId="odr-link">smartodr.in</ExtLink></p>
        </Section>
      </div>
    </section>
  </div>
);

export default Grievance;
